import { Box, Typography, Alert, Grid, FormControlLabel, Checkbox } from '@mui/material';
import React, { FC, useState, useEffect, Fragment, useMemo } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { acceptOrderTemplate, getOrderDetailsTemplate } from 'apollo/templates/order';
import StatusDatePicker from 'components/orders/order-details/status-date-picker.component';
import IntExtDeliveryDatePicker from 'components/orders/order-details/int-ext-delivery-date-picker.component';
import SampleMethod from 'components/lab-order/sample-method/sample-method.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Order, Sample } from 'models/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { LoadingButton } from '@mui/lab';
import Loader from 'components/shared/loader/loader.component';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import ProductionScheduling from 'components/shared/production-scheduling/production-scheduling.component';
import { ConstructionForTemplateMatching } from 'components/shared/production-scheduling/scheduling-actions.component';
import { stepsToProductionPlanInput } from 'components/shared/production-scheduling/production-scheduling.utils';
import { ProductionPlanningStep } from 'models/production-planning';
import { DepartmentsProvider } from 'context/departments-context';
import { ProductionPlanningProvider } from 'context/production-planning-context';
import { useDeliveryDates } from 'hooks/useDeliveryDates';
import { panNumbersTemplate, panNumberFeatureStatusTemplate } from 'apollo/templates/pan-number';
import { PanNumber } from 'models/pan-number';
import PanNumberSelect from 'components/shared/pan-number-select/pan-number-select.component';
import { useReceivedByLabWorksheet } from 'components/orders/order-details/use-received-by-lab-worksheet';

type ReceivedByLabDialogProps = {
    onUpdate?: (acceptOrder: Order) => void;
    onClose: () => void;
    open: boolean;
    orderId?: string;
};

const ReceivedByLabDialog: FC<ReceivedByLabDialogProps> = ({
    onUpdate,
    onClose,
    open,
    orderId,
}: ReceivedByLabDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const currentOrg = currentOrgVar();
    const [date, setDate] = useState<Date | null>();
    const [sample, setSample] = useState<Sample>({
        type: '',
        content: [],
        otherContent: '',
    });
    const [isLoading, setLoading] = useState(false);
    const [order, setOrder] = useState<Order>();
    const [planSteps, setPlanSteps] = useState<ProductionPlanningStep[]>([]);
    const [panNumbers, setPanNumbers] = useState<PanNumber[]>([]);
    const [panNumberEnabled, setPanNumberEnabled] = useState(false);
    const [selectedPanNumber, setSelectedPanNumber] = useState<PanNumber | undefined>(undefined);
    const { showBoundary } = useDenthubErrorBoundary();
    const { isWorksheetChecked, changeWorksheetChecked, downloadWorksheet } = useReceivedByLabWorksheet();
    const {
        internal,
        external,
        setInternalDate,
        setExternalDate,
        loading: deliveryDatesLoading,
    } = useDeliveryDates(order, showBoundary);

    const constructions = useMemo((): ConstructionForTemplateMatching[] => {
        // DD-2506: the rows are the order's actions (lab-priced, with quantities).
        return (order?.restorations || []).map((restoration) => ({
            actions: (restoration.actions || [])
                .filter((action) => action.actionId)
                .map((action) => ({
                    actionId: action.actionId as string,
                    quantity: action.price?.quantity ? action.price.quantity.toString() : '1',
                })),
        }));
    }, [order?.restorations]);

    useQuery(panNumbersTemplate, {
        onCompleted: ({ panNumbers: fetchedPanNumbers }) => {
            setPanNumbers(fetchedPanNumbers);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useQuery(panNumberFeatureStatusTemplate, {
        onCompleted: ({ currentLab }) => {
            setPanNumberEnabled(currentLab?.panNumberEnabled ?? false);
            if (currentLab?.panNumberEnabled && order?.panNumber) {
                setSelectedPanNumber({ value: order.panNumber, description: order.panNumberDescription });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const isSampleMethodEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.SAMPLE_METHOD,
    );
    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );
    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING,
    );

    const [getOrder, { loading }] = useLazyQuery(getOrderDetailsTemplate, {
        onCompleted({ order }) {
            setOrder(order);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        getOrder({ variables: { orderId } });
    }, [orderId]);

    useEffect(() => {
        if (order?.deliveryDetails?.sample) {
            setSample(order.deliveryDetails.sample);
        }
    }, [order]);

    const [changeStatus] = useMutation(acceptOrderTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    // Not in onCompleted: Apollo does not await it, so the button would re-enable mid-download.
    // The parent is updated before the download so a slow worksheet never hides the new status.
    const completeReceive = async (acceptedOrder: Order) => {
        onUpdate?.(acceptedOrder);
        if (isWorksheetChecked) {
            await downloadWorksheet(acceptedOrder);
        }
        onClose();
    };

    const handleChangeStatus = async () => {
        setLoading(true);
        delete (sample as any).comments;
        delete (sample as any).created;

        const productionPlan =
            isProductionPlanningEnabled && planSteps.length > 0 ? stepsToProductionPlanInput(planSteps) : undefined;

        const { data } = await changeStatus({
            variables: {
                orderId: order?._id,
                sample: sample,
                date: date,
                internalDeliveryDate: internal.date,
                externalDeliveryDate: external.date,
                productionPlan: productionPlan,
                panNumber: panNumberEnabled ? selectedPanNumber?.value : undefined,
                panNumberDescription: panNumberEnabled ? selectedPanNumber?.description : undefined,
            },
        });
        if (data?.acceptOrder) {
            await completeReceive(data.acceptOrder);
            return;
        }
        setLoading(false);
    };

    const onSampleSelection = (newSample: Sample) => {
        if (newSample.type !== sample.type) {
            setPlanSteps([]);
        }
        setSample(newSample);
    };

    if (deliveryDatesLoading) {
        return <Loader />;
    }

    return (
        <DHDialog
            fullWidth
            maxWidth={isProductionPlanningEnabled ? 'xl' : 'lg'}
            open={open}
            onClose={onClose}
            title={translate('orders:RECEIVE_THE_ORDER')}
            actions={
                <Fragment>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isWorksheetChecked}
                                onChange={(event) => changeWorksheetChecked(event.target.checked)}
                                data-test="received-by-lab-dialog.worksheet-checkbox"
                            />
                        }
                        label={translate('orders:DOWNLOAD_WORKSHEET')}
                    />
                    <LoadingButton
                        disabled={loading || (panNumberEnabled && !selectedPanNumber)}
                        variant="contained"
                        onClick={handleChangeStatus}
                        loading={isLoading}
                        data-test="received-by-lab-dialog.change-status-button"
                    >
                        {translate('orders:CHANGE_THE_STATUS')}
                    </LoadingButton>
                </Fragment>
            }
        >
            <Box mb={3}>
                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('orders:ARE_YOU_SURE_THAT_YOU_WANT_TO_CHANGE_THE_ORDER_STATUS_TO')}{' '}
                    <strong>{translate('orders:ACCEPTED_BY_LAB')}?</strong>
                </Alert>
            </Box>
            <Box mb={2}>
                {panNumberEnabled && (
                    <Grid container spacing={2}>
                        <Grid item sm={12} md={6}>
                            <PanNumberSelect
                                panNumbers={panNumbers}
                                selectedPanNumber={selectedPanNumber?.value}
                                onPanNumberChange={setSelectedPanNumber}
                            />
                        </Grid>
                    </Grid>
                )}
                <Grid container spacing={2} sx={{ mt: panNumberEnabled ? 0 : 2 }}>
                    <Grid item sm={12} md={6}>
                        <Box mb={1}>
                            <Typography variant="h3" mb={1}>
                                {translate('orders:WHEN_WAS_THE_ORDER_RECEIVED')}
                            </Typography>
                        </Box>
                        <StatusDatePicker onChangeDate={setDate} />
                    </Grid>
                    <Grid item sm={12} md={6}>
                        <Box mb={1}>
                            <Typography variant="h3" mb={1}>
                                {translate('orders:INTERNAL_DELIVERY_DATE')}
                            </Typography>
                        </Box>
                        <IntExtDeliveryDatePicker
                            selectedDate={internal.date}
                            deliveryDaysAdvance={internal.daysAdvance}
                            clinicDeliveryDate={order?.deliveryDetails?.date}
                            onChangeDate={setInternalDate}
                            dataTest="received-by-lab-dialog.internal-delivery-date-picker"
                        />
                    </Grid>

                    <Fragment>
                        {isSampleMethodEnabled ? (
                            <Grid item sm={12} md={6}>
                                <Fragment>
                                    <Box>
                                        <Typography variant="h3" mb={1}>
                                            {translate('orders:HOW_WAS_THE_SAMPLE_TAKEN')}
                                        </Typography>
                                    </Box>
                                    <SampleMethod
                                        handleSampleSelection={onSampleSelection}
                                        sample={sample}
                                        dataTest="received-by-lab-dialog.sample-method"
                                    />
                                </Fragment>
                            </Grid>
                        ) : undefined}
                        {isExternalLabUsersEnabled ? (
                            <Grid item sm={12} md={6}>
                                <Box mb={1}>
                                    <Typography variant="h3" mb={1}>
                                        {translate('orders:EXTERNAL_DELIVERY_DATE')}
                                    </Typography>
                                </Box>
                                <IntExtDeliveryDatePicker
                                    selectedDate={external.date}
                                    deliveryDaysAdvance={external.daysAdvance}
                                    clinicDeliveryDate={order?.deliveryDetails?.date}
                                    onChangeDate={setExternalDate}
                                    isExternal={true}
                                    dataTest="received-by-lab-dialog.external-delivery-date-picker"
                                />
                            </Grid>
                        ) : undefined}
                    </Fragment>
                </Grid>
            </Box>

            {isProductionPlanningEnabled && (
                <Box mt={3}>
                    <Typography variant="h3" mb={2}>
                        {translate('orders:PRODUCTION_PLANNING')}
                    </Typography>
                    <ProductionPlanningProvider>
                        <DepartmentsProvider>
                            <ProductionScheduling
                                steps={planSteps}
                                onStepsChange={setPlanSteps}
                                internalDeliveryDate={internal.date}
                                constructions={constructions}
                                sampleType={sample.type}
                                disableAutoTemplate={(order?.iteration ?? 1) > 1}
                                clinicId={order?.customer?._id}
                            />
                        </DepartmentsProvider>
                    </ProductionPlanningProvider>
                </Box>
            )}
        </DHDialog>
    );
};

export default ReceivedByLabDialog;
